import { Component, Event, EventEmitter, h, Host, Prop } from '@stencil/core'
import { IIndividualCue, ISpeaker } from 'src/call/types'
import { Mark } from 'src/helpers/mark'
import { classes, colorClasses } from 'src/utils/css'

import { AnnotationStore, isLastNoteCue, isNoteCue, isSelected } from '../../annotation'
import { CallStore, TranscriptCue, TranscriptStore } from '../../state'
import { darkened, DragButton, selection } from '../ui'

@Component({ tag: 'page-call-detail-transcript-quote' })
export class Quote {
  @Event() addBookmark: EventEmitter<HTMLElement>
  @Event() cueEdited: EventEmitter<{ cue: IIndividualCue; text: string }>
  @Event() cuesSaved: EventEmitter
  @Event() durationTrimmed: EventEmitter<{ start_time: number; end_time: number }>
  @Event() dragActive: EventEmitter<boolean>
  @Event() playCue: EventEmitter
  @Event() startDrag: EventEmitter
  @Event() speakerChanged: EventEmitter<ISpeaker>

  @Prop() annotate = false
  @Prop() cue: TranscriptCue
  @Prop() cueColor: string
  @Prop() host = false
  @Prop() isEditCue = false
  @Prop() isEditing = false
  @Prop() isSavingCues = false
  @Prop() isProcessing = false
  @Prop() isTrimming = false
  @Prop() showHeader = false
  @Prop() showAddMenu = false
  @Prop() hasAddMenu = false
  @Prop() firstSpeakerCue: TranscriptCue
  @Prop() isActive = false
  @Prop() speakers: ISpeaker[] = []

  render() {
    if (!this.cue) {
      return
    }
    return (
      <Host class='flex flex-col w-full'>
        {this.showHeader && (
          <figcaption
            class={`
              relative self-stretch sm:-mx-4 sm:px-4 -mt-1 pb-1 transition duration-200
              ${classes(this.host, 'text-right')}
            `}
            style={{
              filter: this.isEditing ? 'brightness(30%)' : null,
            }}
          >
            {this.annotate && (isNoteCue(this.cue) || isSelected(this.cue)) && (
              <div
                class={`
                  absolute top-0 left-0 h-full border-l-3 text-transparent -ml-7px sm:-ml-2px
                  ${colorClasses({ border: this.cueColor })}
                `}
              />
            )}
            <cite class='ml-3 sm:ml-0 text-sm not-italic font-bold text-surface-higher'>
              {this.firstSpeakerCue.speaker_name}
            </cite>
          </figcaption>
        )}
        {this.cue.text && (
          <blockquote
            class={`
              transition-all duration-200 group relative flex flex-col self-stretch justify-center pl-8 pr-4 -mr-4 -ml-8 pb-1
              ${classes(isLastNoteCue(this.cue), 'pb-3')}
              ${classes((this.isEditing && !this.isEditCue) || this.isTrimming, 'pointer-events-none')}
            `}
            style={{
              filter: (this.isEditing && !this.isEditCue) || this.isTrimming ? 'brightness(30%)' : null,
            }}
          >
            <p
              class={`
                sm:ml-0 ml-3 transition duration-200 text-sm font-medium cursor-pointer rounded-b-md w-4/5 md:w-3/5 relative group
                ${classes(AnnotationStore.state.startCue, 'pointer-events-none')}
                ${classes(this.isProcessing, darkened)}
                ${classes(
                  this.cue !== TranscriptStore.state.activeCue && !this.isEditing && !this.isActive,
                  'bg-opacity-50',
                  'bg-opacity-100'
                )}
                ${classes(this.host, 'self-end', 'self-start')}
                ${classes(!this.isEditCue, selection)}
                ${classes(this.isEditCue && this.isSavingCues, darkened)}
                ${classes(this.isEditCue, 'scale-110 p-2 z-10 bg-surface-lower border', 'px-2 py-1')}
                ${classes(this.showHeader, this.host ? 'rounded-tl-md' : 'rounded-tr-md', 'rounded-t-md')}
                ${colorClasses({ bg: !this.isEditCue && this.cueColor, border: this.cueColor })}
              `}
              onClick={(e) => {
                e.stopImmediatePropagation()
                if (this.isEditing) {
                  return false
                }

                if (!window.getSelection || window.getSelection().toString().length === 0) {
                  this.playCue.emit()
                }
              }}
            >
              {this.isEditCue ? (
                this.cue.cues.map((realCue) => (
                  <ul class='flex flex-col' onClick={(e) => e.stopImmediatePropagation()}>
                    <li
                      class='p-2 m-1 font-semibold text-black bg-white focus:shadow-outline transition cursor-text mousetrap'
                      contenteditable
                      onInput={(event: InputEvent) => {
                        const text = (event.target as HTMLSpanElement).textContent
                        if (text !== realCue.text) {
                          this.cueEdited.emit({ cue: realCue, text })
                        }
                      }}
                      onKeyDown={(event: KeyboardEvent) => {
                        if (event.key === 'Enter') {
                          this.cuesSaved.emit()
                          event.preventDefault()
                        }
                      }}
                    >
                      {realCue.text}
                    </li>
                  </ul>
                ))
              ) : (
                <Mark query={TranscriptStore.state.searchQuery}>{this.cue.cues.map((c) => c.text).join(' ')}</Mark>
              )}

              {(TranscriptStore.state.replaceCuesUrl || TranscriptStore.state.trimRecordingUrl) &&
                CallStore.state.call.state === 'finalized' && (
                  <menu
                    class={`
                  text-surface-high p-0 my-0 mx-1 absolute top-0 h-full flex items-center transition duration-200
                    ${classes(this.host, 'flex-row-reverse')}
                    ${classes(!this.isActive && !this.isEditCue && !this.isTrimming, 'opacity-0')}
                  `}
                    style={{
                      left: this.host ? null : '100%',
                      right: this.host ? '100%' : null,
                    }}
                    onClick={(e) => e.stopImmediatePropagation()}
                  >
                    <call-detail-quote-menu
                      processing={this.isProcessing}
                      cue={this.cue}
                      speakers={this.speakers}
                      allowEdit={!!TranscriptStore.state.replaceCuesUrl}
                      allowDelete={!!TranscriptStore.state.trimRecordingUrl}
                      showDeleteBefore={
                        CallStore.state.call.recording.duration_seconds / 2 >= this.cue.start_time &&
                        this.cue.id !== TranscriptStore.state.cues[0].id
                      }
                      showDeleteAfter={
                        CallStore.state.call.recording.duration_seconds / 2 < this.cue.start_time &&
                        this.cue.id !== TranscriptStore.state.cues[TranscriptStore.state.cues.length - 1].id
                      }
                      isEditing={this.isEditing}
                      onEdit={() => {
                        if (this.isEditCue) {
                          this.cuesSaved.emit()
                        } else {
                          AnnotationStore.state.editCue = this.cue
                        }
                      }}
                      onDelete={() => {
                        this.durationTrimmed.emit({
                          start_time: this.cue.start_time,
                          end_time: this.cue.end_time,
                        })
                      }}
                      onDeleteAfter={() => {
                        this.durationTrimmed.emit({
                          start_time: this.cue.end_time,
                          end_time: CallStore.state.call.recording.duration_seconds,
                        })
                      }}
                      onDeleteBefore={() => {
                        this.durationTrimmed.emit({
                          start_time: 0,
                          end_time: this.cue.start_time,
                        })
                      }}
                      onConfirmEdit={() => {
                        this.cuesSaved.emit()
                      }}
                      onSpeakerSelected={({ detail }) => {
                        this.speakerChanged.emit(detail)
                      }}
                    />
                  </menu>
                )}
            </p>

            <div
              class={`
                text-transparent absolute top-0 left-0 ml-6 pl-3px sm:ml-18px sm:pl-0 transition duration-200
                ${colorClasses({ border: this.cueColor })}
                ${classes(
                  (isLastNoteCue(this.cue) && this.annotate) ||
                    (this.annotate && isNoteCue(this.cue)) ||
                    isSelected(this.cue),
                  'border-r-3',
                  'border-0'
                )}
                ${classes(isLastNoteCue(this.cue) && this.annotate, 'bottom-0 mb-2', 'h-full')}
              `}
              style={{
                filter: this.isEditing && this.isEditCue ? 'brightness(30%)' : null,
              }}
            />

            {!isSelected(this.cue) && this.hasAddMenu && (
              <div class='flex absolute left-0'>
                <button
                  onClick={(e) => {
                    e.stopImmediatePropagation()
                    if (this.isProcessing) {
                      return
                    }
                    this.addBookmark.emit(e.target as HTMLElement)
                  }}
                  class={`
                    relative sm:ml-8px ml-23px my-auto flex text-sm leading-none border-2 transform p-3px transition duration-200 z-10
                    ${classes(this.showAddMenu, 'opacity-100', 'opacity-0')}
                    ${classes(
                      AnnotationStore.state.selectedCues?.[0] === this.cue,
                      AnnotationStore.state.processingBookmark
                        ? `
                            rounded rotate-45
                            ${colorClasses({
                              bg: AnnotationStore.state.processingBookmark.hex_color,
                              border: AnnotationStore.state.processingBookmark.hex_color,
                            })}
                            `
                        : 'border-link bg-link text-black rounded-full',
                      'bg-black text-link border-link hover:bg-link hover:text-black rounded-full'
                    )}
                `}
                  hidden={!!AnnotationStore.state.editCue}
                >
                  <ui-icon
                    icon='plus-lg'
                    class={`
                        transition duration-200
                        ${classes(this.isProcessing, 'opacity-0')}
                      `}
                  />
                  {this.isProcessing && <ui-spinner class='absolute text-black' />}
                </button>
              </div>
            )}

            {isLastNoteCue(this.cue) && this.annotate && (
              <DragButton
                class={`
                absolute bottom-0 left-0 -mb-1 ml-5 sm:ml-2 text-surface-base transition
                ${colorClasses({ bg: this.cueColor })}
                ${classes(this.isEditing && this.isEditCue, 'opacity-0')}
              `}
                direction='down'
                disabled={CallStore.state.readonly}
                onMouseEnter={() => this.dragActive.emit(true)}
                onMouseLeave={() => this.dragActive.emit(false)}
                onMouseDown={() => this.startDrag.emit()}
              />
            )}
          </blockquote>
        )}
      </Host>
    )
  }
}
